import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { MoreHorizontalIcon, PlusIcon, TagIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label as FieldLabel } from '@/components/ui/label';
import {
  LabelChip,
  countLabelUsage,
  orderLabelsForCuration,
  useDeleteLabel,
  useUpdateLabel,
} from '@/features/labels';
// By path: the search helper is not part of the labels package's public face.
import { matchLabelsByName } from '@/features/labels/labelName';
import type {
  LabelColor,
  LabelView,
  TaskView,
} from '@/lib/api/generated/model';
import { useCanWork } from '@/features/workspaces/canWork';
import { ListCard } from '@/shared/components/ListCard';
import { LIST_ROW, LIST_ROWS } from '@/shared/components/listRow';

import { CreateLabelDialog, LABEL_NAME_MAX } from './CreateLabelDialog';
import { LabelColorChoice } from './LabelColorChoice';

export interface LabelsSectionProps {
  workspaceId: string;
  /** The workspace's whole vocabulary, in the order the contract gave it. */
  registry: readonly LabelView[];
  /** Every task in the workspace — the only source of a usage count. */
  tasks: readonly TaskView[];
}

/**
 * How many tasks carry a label, in words rather than a bare figure.
 *
 * "No tasks" rather than "0 tasks", because the row this appears in is read as
 * a sentence about the label and a zero reads as a measurement that failed.
 */
function usageSentence(count: number): string {
  if (count === 0) return 'No tasks';
  if (count === 1) return '1 task';
  return `${String(count)} tasks`;
}

/**
 * Settings → Labels (spec 228): the workspace's label vocabulary on the shared
 * list card, read and acted on the way People is — a search, "N of M labels",
 * one row per label with its usage, and a "…" menu for Rename and Delete.
 * "New label" heads the tab where People's "Add member" does.
 *
 * Counts are derived here from the aggregate rather than fetched: no endpoint
 * provides them, and the delete confirmation cannot honestly be written without
 * one. A label no task carries is absent from that map, which is why every row
 * looks its own count up with a default rather than iterating the map.
 */
export function LabelsSection({
  workspaceId,
  registry,
  tasks,
}: LabelsSectionProps) {
  const usage = useMemo(() => countLabelUsage(tasks), [tasks]);
  // Read by anyone who reaches Settings, changed by its members (spec 189).
  const canWork = useCanWork();

  // Most-used first, unused last — see `orderLabelsForCuration`.
  const ordered = useMemo(
    () => orderLabelsForCuration(registry, usage),
    [registry, usage],
  );

  const [query, setQuery] = useState('');
  const shown = useMemo(
    () => matchLabelsByName(ordered, query),
    [ordered, query],
  );

  const [editing, setEditing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<LabelView | null>(null);
  const [creating, setCreating] = useState(false);

  const openCreate = () => {
    setCreating(true);
  };

  return (
    <div className="flex flex-col gap-4">
      {canWork ? (
        // A phone gives the tab's main action the full width (spec 276).
        <div className="flex justify-end max-md:[&>*]:w-full">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCreate}
          >
            <PlusIcon aria-hidden="true" />
            New label
          </Button>
        </div>
      ) : null}

      <ListCard
        title="Labels"
        count={registry.length}
        headingId="labels-heading"
        description="The words this workspace uses to say what a task is about. Renaming one updates every task carrying it."
        noun={['label', 'labels']}
        state={registry.length === 0 ? 'empty' : 'idle'}
        empty={{
          icon: TagIcon,
          description:
            'No labels yet. Create one here, or from a task’s Labels field.',
          action: canWork ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openCreate}
            >
              New label
            </Button>
          ) : undefined,
        }}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: 'Search by name',
          label: 'Search labels',
        }}
        shown={shown.length}
        total={registry.length}
        noResults={{
          description: `No label matches “${query.trim()}”.`,
          onClear: () => {
            setQuery('');
          },
        }}
      >
        <ul aria-label="Labels" className={LIST_ROWS}>
          {shown.map((label) => {
            const count = usage.get(label.id) ?? 0;
            return (
              <li key={label.id} className={LIST_ROW}>
                {/*
                  The chip, not a plain string: this page is where someone
                  decides what a label should look like, so it has to show the
                  thing itself rather than a description of it.
                */}
                <LabelChip label={label} />
                {/*
                  The count answers "how many"; the link answers "which".
                  Overview reads the same `labels` parameter the board does, and
                  it spans every workflow — the right destination for a question
                  asked about the workspace's whole vocabulary.
                */}
                {count > 0 ? (
                  <Link
                    to="/workspaces/$workspaceId"
                    params={{ workspaceId }}
                    search={{ labels: label.id }}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring touch-pad rounded text-xs underline underline-offset-2 transition-colors focus-visible:ring-3 focus-visible:outline-none"
                  >
                    {usageSentence(count)}
                  </Link>
                ) : (
                  <span className="text-muted-foreground text-xs">
                    {usageSentence(0)}
                  </span>
                )}
                {canWork ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="ms-auto shrink-0"
                        >
                          <MoreHorizontalIcon aria-hidden="true" />
                          <span className="sr-only">
                            Actions for {label.name}
                          </span>
                        </Button>
                      }
                    />
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setEditing(label.id);
                        }}
                      >
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => {
                          setConfirming(label);
                        }}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}

                {editing === label.id ? (
                  <div className="basis-full">
                    <LabelEditor
                      workspaceId={workspaceId}
                      label={label}
                      onDone={() => {
                        setEditing(null);
                      }}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </ListCard>

      <CreateLabelDialog
        workspaceId={workspaceId}
        registry={registry}
        open={creating}
        onOpenChange={setCreating}
      />

      {confirming ? (
        <DeleteConfirmation
          workspaceId={workspaceId}
          label={confirming}
          count={usage.get(confirming.id) ?? 0}
          onClose={() => {
            setConfirming(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Inline rename and slot choice for one label.
 *
 * Inline rather than in a dialog: this is a one-field edit on a row that is
 * already on screen, and a dialog would hide the rest of the vocabulary at
 * exactly the moment someone is trying to keep a name distinct from it.
 */
function LabelEditor({
  workspaceId,
  label,
  onDone,
}: {
  workspaceId: string;
  label: LabelView;
  onDone: () => void;
}) {
  const [name, setName] = useState(label.name);
  const [color, setColor] = useState<LabelColor>(label.color);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateLabel();

  const fieldId = `label-name-${label.id}`;
  const field = useRef<HTMLInputElement>(null);
  // Opened from the row's menu, so the field takes the focus the menu gives up.
  useEffect(() => {
    field.current?.focus();
  }, []);

  function save() {
    setError(null);
    update.mutate(
      { workspaceId, labelId: label.id, name: name.trim() },
      {
        onSuccess: onDone,
        // The editor is left standing on the refused name. A rename is refused
        // for exactly one reason a reader can act on — the word is taken — and
        // closing the field would take away the word they now have to change.
        onError: (failure) => {
          setError(failure.message);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-2 pb-1">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor={fieldId}>Name</FieldLabel>
          <Input
            ref={field}
            id={fieldId}
            value={name}
            maxLength={LABEL_NAME_MAX}
            aria-invalid={error ? true : undefined}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={update.isPending || name.trim().length === 0}
          onClick={save}
        >
          Save
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onDone}>
          Cancel
        </Button>
      </div>

      <LabelColorChoice
        name={`color-${label.id}`}
        value={color}
        onChange={(slot) => {
          const previous = color;
          setColor(slot);
          setError(null);
          update.mutate(
            { workspaceId, labelId: label.id, color: slot },
            {
              // Put the radio back. Leaving it on a slot the service never
              // stored would leave the reader looking at a colour this label
              // does not have, and believing it until the next refetch
              // silently disagreed.
              onError: (failure) => {
                setColor(previous);
                setError(failure.message);
              },
            },
          );
        }}
      />

      {error ? (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The confirmation, which exists to say what deleting costs.
 *
 * The count is stated before anything is sent (SC-007). "Are you sure?" without
 * a number is not a confirmation — it is a second click.
 */
function DeleteConfirmation({
  workspaceId,
  label,
  count,
  onClose,
}: {
  workspaceId: string;
  label: LabelView;
  count: number;
  onClose: () => void;
}) {
  const remove = useDeleteLabel();
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  /*
    Cancel takes the initial focus, so the destructive control is never one
    stray Enter away.

    That is all this hook does now. Moving focus in and putting it back used to
    be here too, hand-written beside a hand-written `aria-modal`, an `Escape`
    handler and a full-screen backdrop button — every one of which the dialog
    primitive below provides, correctly, and shares with the other fifteen
    dialogs in this app.
  */
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !remove.isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete “{label.name}”?</DialogTitle>
          <DialogDescription>
            {count === 0
              ? 'No tasks carry this label. Deleting it removes the word from this workspace.'
              : `It will be removed from ${usageSentence(count).toLowerCase()}. Those tasks are not deleted, and this cannot be undone.`}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            ref={cancelRef}
            type="button"
            variant="outline"
            disabled={remove.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={remove.isPending}
            onClick={() => {
              setError(null);
              remove.mutate(
                { workspaceId, labelId: label.id },
                {
                  onSuccess: onClose,
                  onError: (failure) => {
                    setError(failure.message);
                  },
                },
              );
            }}
          >
            {remove.isPending ? 'Deleting…' : 'Delete label'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
